import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { isHomeRoute } from '~/lib/chat-origin';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useFeatureFlag, VOICE_HANDS_FREE_FLAG } from '~/hooks/queries/use-feature-flags';
import { useVoiceCall, type HandsFreeCallHooks, type VoiceCallState, type VoiceTurn } from '../use-voice-call';
import type { VoiceStatus } from '../voice-event-router';
import { persistCallEnded } from '../persist-call-ended';
import {
  buildScreenContext,
  formatScreenNote,
  planUiNavigate,
  screenLabel,
  type ScreenContext,
  type ScreenItem,
} from './screen';
import { useHandsFreeActivity, useQuietCallClock, useScreenNoteSender } from './use-hands-free-watch';

/**
 * FRONT-254: the hands-free call. With `eddy.voice-hands-free` on, a call
 * started from a chat window is owned HERE, at the app shell, instead of by
 * that window's composer — so it survives closing the window and moving
 * between pages. The composer only shows and steers it (`attachComposer`);
 * while no composer for the call's chat is on screen, the strip at the top of
 * the app does (`HandsFreeCallStrip`).
 *
 * The call still belongs to the chat it started in: every turn, the recap and
 * the save card land there (`binding.sessionId`), exactly as before.
 */

export interface HandsFreeBinding {
  sessionId: string;
  branchId: string;
  assetContext?: string;
  scopeEntityId?: string;
  /** The starting chat's title, for the strip ("from 'Pricing'"). */
  title: string | null;
}

export interface HandsFreeCallValue {
  /** The flag is on for this person. Off: nothing below is ever used. */
  enabled: boolean;
  /** A hands-free call is connecting or live. */
  active: boolean;
  binding: HandsFreeBinding | null;
  state: VoiceCallState;
  status: VoiceStatus;
  muted: boolean;
  toggleMute: () => void;
  stop: () => void;
  /** The newest words each side said, for the strip and the composer bar. */
  userLine: string;
  assistantLine: string;
  /** EDDY's last finished sentence — what the strip keeps showing. */
  lastSaid: string;
  /** When the call went live (ms epoch), for the timer. */
  startedAt: number | null;
  startCall: (binding: HandsFreeBinding) => void;
  /** A composer bound to `sessionId` is on screen. Returns its detach. */
  attachComposer: (sessionId: string) => () => void;
  /** A composer for the call's own chat is on screen — the strip hides. */
  composerAttached: boolean;
  /** An open window (chat, document, file) on top of the page. Returns its clear. */
  showOpenItem: (item: ScreenItem) => () => void;
  screen: ScreenContext;
  screenLabel: string;
  /** Seconds left before a quiet call ends, while the warning shows. */
  quietSecondsLeft: number | null;
  keepCall: () => void;
  /** Reopen the chat the call started in. */
  openStartingChat: () => void;
}

const HandsFreeCallContext = createContext<HandsFreeCallValue | null>(null);

/** Null outside the provider (the phone surface, tests) — callers fall back
 *  to their own pre-FRONT-254 call. */
export function useHandsFreeCall(): HandsFreeCallValue | null {
  return useContext(HandsFreeCallContext);
}

export function HandsFreeCallProvider({ children }: { children: ReactNode }) {
  const enabled = useFeatureFlag(VOICE_HANDS_FREE_FLAG, false);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);

  const [binding, setBinding] = useState<HandsFreeBinding | null>(null);
  const bindingRef = useRef<HandsFreeBinding | null>(null);
  bindingRef.current = binding;
  const pendingStartRef = useRef(false);

  const [userLine, setUserLine] = useState('');
  const [assistantLine, setAssistantLine] = useState('');
  const [lastSaid, setLastSaid] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);

  // ── What is on screen ────────────────────────────────────────────────
  const [openItems, setOpenItems] = useState<{ token: number; item: ScreenItem }[]>([]);
  const tokenRef = useRef(0);
  const showOpenItem = useCallback((item: ScreenItem) => {
    const token = ++tokenRef.current;
    setOpenItems((prev) => [...prev.filter((e) => e.token !== token), { token, item }]);
    return () => setOpenItems((prev) => prev.filter((e) => e.token !== token));
  }, []);
  const openItem = openItems.length > 0 ? openItems[openItems.length - 1].item : null;
  const screen = useMemo(() => buildScreenContext(location.pathname, openItem), [location.pathname, openItem]);
  const note = formatScreenNote(screen);
  const noteRef = useRef(note);
  noteRef.current = note;
  const locationRef = useRef(location);
  locationRef.current = location;

  // ── Activity clock (shared with the phone, use-hands-free-watch.ts) ──
  const activity = useHandsFreeActivity();
  const { reset: resetActivity } = activity;

  // ── Screen moves ─────────────────────────────────────────────────────
  const onClientAction = useCallback(
    async (args: unknown): Promise<{ ok: true } | { ok: false; error: string }> => {
      const plan = planUiNavigate(args);
      if (plan.kind === 'refuse') return { ok: false, error: plan.error };
      if (plan.kind === 'route') {
        navigate(plan.path);
        return { ok: true };
      }
      // A chat opens in the chat window, which lives on Home. Check it is
      // the person's own first — the route is owner-scoped and 404s for
      // anything else — so a made-up id never opens an empty window.
      let title: string | null = null;
      try {
        const data = await api.get<{ thread?: { title?: string | null } }>(
          `/api/chats/${encodeURIComponent(plan.sessionId)}`,
        );
        title = data.thread?.title ?? null;
      } catch {
        return { ok: false, error: "That chat wasn't found, so the screen stayed put." };
      }
      if (!isHomeRoute(locationRef.current.pathname)) navigate('/');
      requestOpenChat(plan.sessionId, title, null);
      return { ok: true };
    },
    [navigate, requestOpenChat],
  );

  const hooks = useMemo<HandsFreeCallHooks>(
    () => ({
      onClientAction,
      getScreenNote: () => noteRef.current,
      ...activity.report,
    }),
    [onClientAction, activity.report],
  );

  const onCallEnded = useCallback(
    (turns: VoiceTurn[]) => {
      const b = bindingRef.current;
      if (!b) return;
      void persistCallEnded(queryClient, { sessionId: b.sessionId, branchId: b.branchId, turns });
    },
    [queryClient],
  );

  const call = useVoiceCall({
    branchId: binding?.branchId ?? 'main',
    sessionId: binding?.sessionId,
    assetContext: binding?.assetContext,
    scopeEntityId: binding?.scopeEntityId,
    onUserTranscript: (text) => setUserLine(text),
    onAssistantDelta: (delta) => setAssistantLine((prev) => prev + delta),
    onAssistantFinal: (text) => {
      setAssistantLine('');
      if (text.trim()) setLastSaid(text.trim());
    },
    onCallEnded,
    handsFree: hooks,
  });
  const active = call.state === 'connecting' || call.state === 'live';

  const startCall = useCallback(
    (next: HandsFreeBinding) => {
      const current = bindingRef.current;
      if (active && current) {
        if (current.sessionId !== next.sessionId) {
          toast.info("You're already on a call. Hang up first to start one here.");
        }
        return;
      }
      setUserLine('');
      setAssistantLine('');
      setLastSaid('');
      setStartedAt(null);
      resetActivity();
      pendingStartRef.current = true;
      setBinding(next);
    },
    [active, resetActivity],
  );

  // Start once the binding has reached the call's own session info (the
  // hook copies its options into a ref in an effect declared before this one).
  const { start } = call;
  useEffect(() => {
    if (!binding || !pendingStartRef.current) return;
    pendingStartRef.current = false;
    void start();
  }, [binding, start]);

  useEffect(() => {
    if (call.state === 'live') setStartedAt((prev) => prev ?? Date.now());
  }, [call.state]);

  // ── Composers on screen ──────────────────────────────────────────────
  const [attached, setAttached] = useState<Record<string, number>>({});
  const attachComposer = useCallback((sessionId: string) => {
    setAttached((prev) => ({ ...prev, [sessionId]: (prev[sessionId] ?? 0) + 1 }));
    return () =>
      setAttached((prev) => {
        const n = (prev[sessionId] ?? 1) - 1;
        const next = { ...prev };
        if (n <= 0) delete next[sessionId];
        else next[sessionId] = n;
        return next;
      });
  }, []);
  const composerAttached = !!binding && (attached[binding.sessionId] ?? 0) > 0;

  // ── Tell EDDY what is on screen, and end a call nobody is using ──────
  useScreenNoteSender(note, call.state === 'live', call.sendScreenNote);
  const { quietSecondsLeft, keepCall } = useQuietCallClock({
    live: call.state === 'live',
    sessionId: binding?.sessionId ?? null,
    stop: call.stop,
    activity,
  });

  const openStartingChat = useCallback(() => {
    const b = bindingRef.current;
    if (!b) return;
    if (!isHomeRoute(locationRef.current.pathname)) navigate('/');
    requestOpenChat(b.sessionId, b.title, null);
  }, [navigate, requestOpenChat]);

  const value = useMemo<HandsFreeCallValue>(
    () => ({
      enabled,
      active,
      binding,
      state: call.state,
      status: call.status,
      muted: call.muted,
      toggleMute: call.toggleMute,
      stop: call.stop,
      userLine,
      assistantLine,
      lastSaid,
      startedAt,
      startCall,
      attachComposer,
      composerAttached,
      showOpenItem,
      screen,
      screenLabel: screenLabel(screen),
      quietSecondsLeft,
      keepCall,
      openStartingChat,
    }),
    [
      enabled,
      active,
      binding,
      call.state,
      call.status,
      call.muted,
      call.toggleMute,
      call.stop,
      userLine,
      assistantLine,
      lastSaid,
      startedAt,
      startCall,
      attachComposer,
      composerAttached,
      showOpenItem,
      screen,
      quietSecondsLeft,
      keepCall,
      openStartingChat,
    ],
  );

  return <HandsFreeCallContext.Provider value={value}>{children}</HandsFreeCallContext.Provider>;
}
